import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { listarFormasPagamento } from '@/integrations/supabase/formas-pagamento';
import { listarContasBancarias } from '@/integrations/supabase/contas-bancarias';

interface DialogProps {
  isOpen: boolean;
  total: number;
  onConfirm: (formaId: string | null, contaId: string | null) => Promise<void>;
  onCancel: () => void;
}

export function DialogSelecionarFormaEConta({ isOpen, total, onConfirm, onCancel }: DialogProps) {
  const [formaId, setFormaId] = React.useState<string>('');
  const [contaId, setContaId] = React.useState<string>('');
  const [isConfirming, setIsConfirming] = React.useState(false);
  const [usarPadrao, setUsarPadrao] = React.useState(false);

  const { data: formas = [] } = useQuery({
    queryKey: ['formas_pagamento'],
    queryFn: listarFormasPagamento
  });

  const { data: contas = [] } = useQuery({
    queryKey: ['contas_bancarias'],
    queryFn: listarContasBancarias
  });

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm(formaId || null, contaId || null);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(aberto) => !aberto && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Registrar Venda</DialogTitle>
          <DialogDescription>
            Selecione a forma de pagamento e a conta onde o dinheiro entrou
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Total */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600">Total da Venda</p>
              <p className="text-2xl font-bold text-blue-600">
                {total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </p>
            </CardContent>
          </Card>

          {/* Forma de Pagamento */}
          <div className="space-y-2">
            <Label htmlFor="forma">💳 Forma de Pagamento</Label>
            {formas.length === 0 ? (
              <div className="space-y-2">
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm space-y-2">
                  <div className="flex gap-2">
                    <AlertCircle className="w-4 h-4 text-yellow-700 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-yellow-800 font-semibold">Nenhuma forma de pagamento configurada</p>
                      <p className="text-yellow-700 text-xs mt-1">
                        Para adicionar formas (Dinheiro, Débito, Crédito, etc.):
                      </p>
                      <ol className="text-yellow-700 text-xs ml-4 mt-1 space-y-1 list-decimal">
                        <li>Abra a página <strong>"Fluxo de Caixa"</strong> (no menu lateral)</li>
                        <li>Clique no botão <strong>"Configurar"</strong> (topo direito da página)</li>
                        <li>Procure por <strong>"Formas de Pagamento"</strong></li>
                        <li>Clique em <strong>"+ Adicionar Forma"</strong></li>
                        <li>Preencha o nome (Espécie, Débito, Crédito, Pix, etc.)</li>
                      </ol>
                    </div>
                  </div>
                </div>
                <Button
                  variant={usarPadrao ? "default" : "outline"}
                  className="w-full"
                  onClick={() => setUsarPadrao(!usarPadrao)}
                >
                  {usarPadrao ? "✅ Registrar como Espécie" : "+ Registrar como Espécie (agora)"}
                </Button>
              </div>
            ) : (
              <Select value={formaId} onValueChange={setFormaId}>
                <SelectTrigger id="forma">
                  <SelectValue placeholder="Selecione a forma de pagamento..." />
                </SelectTrigger>
                <SelectContent>
                  {formas.map((forma) => (
                    <SelectItem key={forma.id} value={forma.id || ''}>
                      {forma.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Conta */}
          <div className="space-y-2">
            <Label htmlFor="conta">🏦 Conta (Onde entrou o dinheiro)</Label>
            {contas.length === 0 ? (
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm space-y-2">
                <div className="flex gap-2">
                  <AlertCircle className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-blue-800 font-semibold">Nenhuma conta configurada</p>
                    <p className="text-blue-700 text-xs mt-1">
                      Para adicionar contas (Caixa da Loja, Banco, etc.):
                    </p>
                    <ol className="text-blue-700 text-xs ml-4 mt-1 space-y-1 list-decimal">
                      <li>Abra a página <strong>"Fluxo de Caixa"</strong> (no menu lateral)</li>
                      <li>Clique no botão <strong>"Configurar"</strong> (topo direito da página)</li>
                      <li>Procure por <strong>"Contas Bancárias"</strong></li>
                      <li>Clique em <strong>"+ Adicionar Conta"</strong></li>
                      <li>Preencha o nome (Caixa da Loja, Banco do Brasil, Santander, etc.)</li>
                    </ol>
                  </div>
                </div>
              </div>
            ) : (
              <Select value={contaId} onValueChange={setContaId}>
                <SelectTrigger id="conta">
                  <SelectValue placeholder="Selecione a conta..." />
                </SelectTrigger>
                <SelectContent>
                  {contas.map((conta) => (
                    <SelectItem key={conta.id} value={conta.id || ''}>
                      {conta.nome} {conta.saldo_inicial > 0 && `(Saldo: ${conta.saldo_inicial.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Info */}
          {(formaId || contaId) && (
            <div className="bg-green-50 border border-green-200 rounded p-3 text-sm">
              <p className="text-green-800">
                ✅ Venda será registrada como:
                {formaId && formas.find(f => f.id === formaId) && (
                  <> <strong>{formas.find(f => f.id === formaId)?.nome}</strong></>
                )}
                {contaId && contas.find(c => c.id === contaId) && (
                  <> em <strong>{contas.find(c => c.id === contaId)?.nome}</strong></>
                )}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isConfirming}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isConfirming || (formas.length > 0 && !formaId) || (contas.length > 0 && !contaId)}
          >
            {isConfirming ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processando...
              </>
            ) : (
              'Confirmar Venda'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
